import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, TrendingUp, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

const CareersHero = () => {
  const [textIndex, setTextIndex] = useState(0);
  const heroTexts = ["We are hiring", "Join Terces family"];

  // Text rotation effect
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % heroTexts.length);
    }, 4000);
    return () => clearInterval(intervalId);
  }, []);

  const benefits = [
    {
      number: "01",
      title: "Boundless Opportunity",
      icon: Sparkles
    },
    {
      number: "02",
      title: "Build Your Future",
      icon: TrendingUp
    },
    {
      number: "03",
      title: "Career Growth",
      icon: Target
    }
  ];

  return (
    <section 
      className="relative w-full min-h-screen overflow-hidden bg-[#F6F4EF] flex items-center"
      style={{ paddingTop: '140px', paddingBottom: '100px' }}
    >
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Ambient Background Effects */}
      <motion.div
        animate={{
          y: [0, -20, 0],
          opacity: [0.03, 0.05, 0.03]
        }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary blur-3xl"
      />
      <motion.div
        animate={{
          y: [0, 20, 0],
          opacity: [0.03, 0.05, 0.03]
        }}
        transition={{ duration: 7, repeat: Infinity }}
        className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-yellow-400 blur-3xl"
      />

      {/* Main Container */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {/* LEFT SIDE - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Small Tag */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block"
            >
              <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
                Careers at Terces
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-[3.5rem] font-extrabold text-gray-900 leading-[1.13]"
              style={{ letterSpacing: '-0.02em' }}
            >
            Build Your Career with{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Purpose</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full h-3"
                  viewBox="0 0 200 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M2 8C50 2, 150 2, 198 8"
                    stroke="#1b7840"
                    strokeWidth="4"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 0.8 }}
                  />
                </svg>
              </span>
              <br />
     
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg md:text-xl text-gray-600 leading-relaxed"
            >
              Join a dynamic, expanding team of professionals that provides excellence.
            </motion.p>

            {/* Benefits List */}
            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="space-y-5 pt-4"
            >
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                    whileHover={{ x: 5 }}
                    className="flex items-center gap-4 group cursor-pointer"
                  >
                    {/* Number Badge */}
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                      transition={{ duration: 0.4 }}
                      className="relative"
                    >
                      <div className="w-12 h-12 rounded-2xl bg-white shadow-md flex items-center justify-center font-bold text-primary border-2 border-primary/20 group-hover:border-primary/40 transition-all duration-300">
                        {benefit.number}
                      </div>
                      {/* Glow effect */}
                      <motion.div
                        className="absolute inset-0 rounded-2xl bg-primary/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={{ scale: 0.8 }}
                        whileHover={{ scale: 1.1 }}
                      />
                    </motion.div>

                    {/* Text and Icon */}
                    <div className="flex items-center gap-3 flex-1">
                      <span className="font-semibold text-gray-900 text-base md:text-lg group-hover:text-primary transition-colors">
                        {benefit.title}
                      </span>
                      <Icon className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </motion.li>
                );
              })}
            </motion.ul>

            {/* Animated CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="pt-4"
            >
              <Link to="/Terces_Recruitment">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative px-9 py-4 bg-primary hover:bg-primary-dark text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3 overflow-hidden"
                >
                  {/* Animated Text */}
                  <span className="relative z-10 flex items-center gap-3 min-w-[180px]">
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={textIndex}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="inline-block"
                      >
                        {heroTexts[textIndex]}
                      </motion.span>
                    </AnimatePresence>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>

                  {/* Shine effect */}
                  <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>

          {/* RIGHT SIDE - Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Main Image Container */}
            <div className="relative">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
                className="relative z-10"
              >
                {/* Replace with your career hero image */}
                <img 
                  src="https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?w=800&h=900&fit=crop"
                  alt="Join Terces Team"
                  className="w-full h-auto rounded-3xl shadow-2xl"
                />
                
                {/* Image Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/20 to-transparent rounded-3xl"></div>
              </motion.div>

              {/* Floating Badge - Top Right */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-6 z-20"
              >
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary">20+</div>
                  <div className="text-sm text-gray-600 mt-1">Years</div>
                  <div className="text-xs text-gray-500">Experience</div>
                </div>
              </motion.div>

              {/* Floating Badge - Bottom Left */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="absolute -bottom-6 -left-6 bg-primary text-white rounded-2xl shadow-xl p-6 z-20"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Join Our</div>
                    <div className="text-lg font-bold">Growing Team</div>
                  </div>
                </div>
              </motion.div>

              {/* Decorative Elements */}
              <motion.div
                animate={{
                  rotate: [0, 180, 360],
                }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -top-12 -left-12 w-24 h-24 border-4 border-primary/20 z-0"
                style={{ transform: 'rotate(45deg)' }}
              />

              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full bg-yellow-400/20 blur-2xl z-0"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-gray-400"
        >
          <span className="text-xs uppercase tracking-wider">Scroll</span>
          <div className="w-0.5 h-8 bg-gradient-to-b from-primary to-transparent"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default CareersHero;